import { cn } from '@/lib/utils';
import { formatTimeSlot } from '@/utils/transformer';
import { useEffect, useState } from 'react';
import { E2E_SCHEDULING } from '@/constants/e2eSchedulingTestIds';
interface TimeSlotSelectProps {
	onValueChange?: (st: string) => void;
	slots?: Array<AvailableDates.SessionTimeSlot>;
	disabled?: boolean;
	defaultValue?: AvailableDates.SessionTimeSlot | null;
	/** Horários já reservados pelo usuário (sessão CREATED), exibidos com selo. */
	pendingReservationSlots?: Array<AvailableDates.SessionTimeSlot>;
	pendingSlotBadgeLabel?: string;
}

export const TimeSlotSelect = ({
	onValueChange,
	slots = [],
	disabled,
	defaultValue = null,
	pendingReservationSlots = [],
	pendingSlotBadgeLabel = 'Sua reserva',
}: TimeSlotSelectProps) => {
	const [selectedSlot, setSelectedSlot] = useState<string | null>(null);

	const handleOnValueChange = (value: string) => {
		const parsedSlot = JSON.parse(value);
		const formattedSlot = formatTimeSlot(
			parsedSlot.start_time,
			parsedSlot.end_time,
		);

		setSelectedSlot(formattedSlot);
		onValueChange?.(value);
	};

	const hasSlots = slots.length > 0;

	useEffect(() => setSelectedSlot(null), [slots]);
	if (!hasSlots || disabled) {
		return (
			<div className="flex w-full items-center justify-center rounded-2xl border border-dashed border-zinc-200 bg-zinc-50 px-4 py-3 text-xs font-medium text-zinc-400">
				Sem horário disponível
			</div>
		);
	}

	return (
		<div className="flex w-full flex-wrap items-center justify-center gap-3">
			{slots.map((slot, index) => {
				const formatted = formatTimeSlot(
					slot.start_time,
					slot.end_time,
				);
				const defaultFormatted =
					defaultValue &&
					formatTimeSlot(
						defaultValue.start_time,
						defaultValue.end_time,
					);
				const isActive =
					selectedSlot === formatted ||
					(!selectedSlot && defaultFormatted === formatted);
				const slotMs = new Date(slot.start_time).getTime();
				const isPendingReservation = pendingReservationSlots.some(
					(p) => new Date(p.start_time).getTime() === slotMs,
				);

				return (
					<button
						key={index}
						type="button"
						data-testid={`${E2E_SCHEDULING.TIME_SLOT_PREFIX}${index}`}
						onClick={() =>
							handleOnValueChange(JSON.stringify(slot))
						}
						className={cn(
							'min-w-[96px] rounded-2xl border px-3 py-2 text-sm font-semibold shadow-sm transition-all',
							'flex flex-col items-center justify-center gap-0.5',
							isActive
								? 'border-stone-900 bg-stone-900 text-white'
								: isPendingReservation
								  ? 'border-amber-300 bg-amber-50 text-amber-950 hover:border-amber-400 hover:bg-amber-100/80'
								  : 'border-zinc-200 bg-white text-zinc-800 hover:border-stone-900/60 hover:bg-zinc-50',
						)}
					>
						<span>{formatted}</span>
						{isPendingReservation && (
							<span
								className={cn(
									'text-[9px] font-black uppercase tracking-wide',
									isActive
										? 'text-white/90'
										: 'text-amber-800',
								)}
							>
								{pendingSlotBadgeLabel}
							</span>
						)}
					</button>
				);
			})}
		</div>
	);
};
